import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useUI } from '../contexts/UIContext'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'

interface EventoItem {
  id: number
  tipo: string
  tipoLabel: string
  dataHora: string
  motorista: string | null
  localizacao: string
}

interface Props {
  ativoId: string
  ativoNome: string
  onClose: () => void
}

function fmtDataHora(iso: string) {
  return new Date(iso).toLocaleString('pt-BR')
}

function iconePorTipo(tipo: string) {
  if (tipo === 'ignitionOn') return { icone: 'ph-power', cor: '#22c55e' }
  if (tipo === 'ignitionOff') return { icone: 'ph-power', cor: '#f87171' }
  return { icone: 'ph-identification-badge', cor: '#0ea5e9' }
}

function hojeISO() {
  return new Date().toISOString().slice(0, 10)
}
function seteDiasAtrasISO() {
  const d = new Date()
  d.setDate(d.getDate() - 7)
  return d.toISOString().slice(0, 10)
}

export default function EventosModal({ ativoId, ativoNome, onClose }: Props) {
  const { toast } = useUI()
  const [eventos, setEventos] = useState<EventoItem[]>([])
  const [carregando, setCarregando] = useState(true)
  const [exportando, setExportando] = useState<'excel' | 'pdf' | null>(null)
  const [dataInicio, setDataInicio] = useState(seteDiasAtrasISO())
  const [dataFim, setDataFim] = useState(hojeISO())

  const token = localStorage.getItem('moobi_token')

  const carregar = useCallback(async () => {
    setCarregando(true)
    try {
      const { data } = await axios.get(`${API}/ativos/${ativoId}/eventos`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { dataInicio, dataFim },
      })
      setEventos(data.eventos || [])
    } catch (e) {
      toast('Erro ao carregar eventos', 'error')
    } finally {
      setCarregando(false)
    }
  }, [ativoId, dataInicio, dataFim, token, toast])

  useEffect(() => { carregar() }, [carregar])

  const exportar = async (formato: 'excel' | 'pdf') => {
    setExportando(formato)
    try {
      const { data, headers } = await axios.get(`${API}/ativos/${ativoId}/eventos/exportar`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { dataInicio, dataFim, formato },
        responseType: 'blob',
      })
      const blob = new Blob([data], { type: String(headers['content-type'] || 'application/octet-stream') })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `eventos_${ativoNome.replace(/\s+/g, '_')}_${Date.now()}.${formato === 'excel' ? 'xlsx' : 'pdf'}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      toast('Erro ao exportar', 'error')
    } finally {
      setExportando(null)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, width: 520, maxWidth: '92vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #1e293b' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="ph-bold ph-clock-counter-clockwise" style={{ fontSize: 18, color: '#0ea5e9' }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>Eventos</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{ativoNome}</div>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: 'rgba(0,0,0,0.3)', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 16, borderRadius: 6, padding: '2px 6px' }}>
            <i className="ph-bold ph-x" style={{ fontSize: 13 }} />
          </button>
        </div>

        {/* Filtro de período */}
        <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid #1e293b', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3 }}>De</div>
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)}
              style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 5, color: '#e2e8f0', fontSize: 12, padding: '5px 7px' }} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3 }}>Até</div>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
              style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 5, color: '#e2e8f0', fontSize: 12, padding: '5px 7px' }} />
          </div>
          <button onClick={carregar}
            style={{ background: '#1e3a6e', border: '1px solid #2d4166', borderRadius: 5, color: '#38bdf8', cursor: 'pointer', fontSize: 12, padding: '6px 10px' }}>
            <i className="ph-bold ph-magnifying-glass" style={{ fontSize: 12, marginRight: 4 }} />Filtrar
          </button>
          <div style={{ flex: 1 }} />
          <button onClick={() => exportar('excel')} disabled={exportando !== null || eventos.length === 0}
            style={{ background: '#14532d', border: '1px solid #166534', borderRadius: 5, color: '#4ade80', cursor: exportando ? 'default' : 'pointer', fontSize: 12, padding: '6px 10px', opacity: eventos.length === 0 ? 0.5 : 1 }}>
            <i className="ph-bold ph-file-xls" style={{ fontSize: 13, marginRight: 4 }} />{exportando === 'excel' ? 'Gerando...' : 'Excel'}
          </button>
          <button onClick={() => exportar('pdf')} disabled={exportando !== null || eventos.length === 0}
            style={{ background: '#450a0a', border: '1px solid #7f1d1d', borderRadius: 5, color: '#f87171', cursor: exportando ? 'default' : 'pointer', fontSize: 12, padding: '6px 10px', opacity: eventos.length === 0 ? 0.5 : 1 }}>
            <i className="ph-bold ph-file-pdf" style={{ fontSize: 13, marginRight: 4 }} />{exportando === 'pdf' ? 'Gerando...' : 'PDF'}
          </button>
        </div>

        {/* Timeline */}
        <div style={{ overflowY: 'auto', padding: '10px 16px', flex: 1 }}>
          {carregando && (
            <div style={{ textAlign: 'center', color: '#64748b', fontSize: 12, padding: '30px 0' }}>Carregando...</div>
          )}
          {!carregando && eventos.length === 0 && (
            <div style={{ textAlign: 'center', color: '#64748b', fontSize: 12, padding: '30px 0' }}>
              <i className="ph-bold ph-tray" style={{ fontSize: 24, display: 'block', marginBottom: 6 }} />
              Nenhum evento no período selecionado
            </div>
          )}
          {!carregando && eventos.map(ev => {
            const { icone, cor } = iconePorTipo(ev.tipo)
            return (
              <div key={ev.id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #1e293b' }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`ph-bold ${icone}`} style={{ fontSize: 14, color: cor }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>{ev.tipoLabel}</span>
                    <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDataHora(ev.dataHora)}</span>
                  </div>
                  {ev.motorista && (
                    <div style={{ fontSize: 11, color: '#0ea5e9', marginTop: 2 }}>
                      <i className="ph-bold ph-user" style={{ fontSize: 11, marginRight: 3 }} />{ev.motorista}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>
                    <i className="ph-bold ph-map-pin" style={{ fontSize: 11, marginRight: 3 }} />{ev.localizacao}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
